import { useCallback, useEffect, useMemo, useState } from "react";

import { useAuth } from "../auth/useAuth";
import { useRuntimeConfig } from "../config/useRuntimeConfig";
import { createMedicationServiceClient, type Medication } from "./client";

export type MedicationListStatus = "idle" | "loading" | "ready" | "error";

// Read-only loader for surfaces that only need medication records (Emergency Health Info).
// Calls only GET /v1/medications -- no upcoming-doses request. Same hook conventions as
// useMedications: runtime-configured base URL, auth.user access token, AbortSignal, loading/error
// state, no cache.
export function useMedicationList() {
  const auth = useAuth();
  const config = useRuntimeConfig();
  const apiBaseUrl = config?.medicationService?.apiBaseUrl;
  const [medications, setMedications] = useState<Medication[]>([]);
  const [status, setStatus] = useState<MedicationListStatus>("idle");
  const [error, setError] = useState<string | null>(null);

  const client = useMemo(() => {
    if (!apiBaseUrl) {
      return null;
    }

    return createMedicationServiceClient(apiBaseUrl, () => auth.user?.access_token);
  }, [apiBaseUrl, auth.user]);

  const loadMedicationList = useCallback(
    async (signal?: AbortSignal) => {
      if (!client) {
        setError("Medication records are not available yet.");
        setStatus("error");
        return;
      }

      setStatus("loading");
      setError(null);

      try {
        setMedications(await client.listMedications(signal));
        setStatus("ready");
      } catch (nextError) {
        if (nextError instanceof DOMException && nextError.name === "AbortError") {
          return;
        }

        setError(nextError instanceof Error ? nextError.message : "We could not load your medication records.");
        setStatus("error");
      }
    },
    [client],
  );

  useEffect(() => {
    if (!auth.isAuthenticated) {
      return undefined;
    }

    const controller = new AbortController();
    void loadMedicationList(controller.signal);

    return () => controller.abort();
  }, [auth.isAuthenticated, loadMedicationList]);

  return {
    error,
    loadMedicationList,
    medications,
    status,
  };
}
